import { Data, List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import FileTypeLookup from '../../FileGroupingFileSpecs/Models/Lookups/FileTypeLookup';
import FileSpecConfigurationView from '../../FileSpecConfigurations/Views/FileSpecConfigurationView';
import ManageColumnSpecComponentVM from '../Components/ManageColumnSpecComponentVM';
import CreateColumnSpecCommand from '../Models/Commands/CreateColumnSpecCommand';
import UpdateColumnSpecCommand from '../Models/Commands/UpdateColumnSpecCommand';
import ColumnConfigurationLookup from '../Models/Lookups/ColumnConfigurationLookup';
import ColumnConfigurationSearchCriteria from '../Models/Queries/ColumnConfigurationSearchCriteria';

@NeoModel
export default class ColumnConfigurationsVM extends Views.ViewModelBase {

    constructor(
        public columnSpecApiClient = AppService.get(Types.ApiClients.ColumnSpecApiClient),
        public fileGroupingFileSpecApiClient = AppService.get(Types.ApiClients.FileGroupingFileSpecApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
    }

    public columnConfigurationSearchCriteria = new ColumnConfigurationSearchCriteria();
    public fileSpecDropdownList = new List(FileTypeLookup);
    public manageColumnSpecComponentVM = new ManageColumnSpecComponentVM();
    public fileSpecID: number = 0;
    public fileSpecProps: string = "";
    public showManageColumnComponent: boolean = false;

    public pageManager = new Data.PageManager(this.columnConfigurationSearchCriteria, ColumnConfigurationLookup,
        this.columnSpecApiClient.getColumnSpecsPagedList, {
            pageSize: 10,
            sortAscending: true,
            sortBy: "columnNumber",
            initialTaskRunner: this.taskRunner,
        });

    public async initialise(fileSpecID?: number, fileSpecProps?: string) {
        this.fileSpecID = Number(fileSpecID);
        this.fileSpecProps = fileSpecProps!;
        this.columnConfigurationSearchCriteria.staticFileSpecID = this.fileSpecID;
        this.columnConfigurationSearchCriteria.fileTypeConcatenated = fileSpecProps!;

        await this.loadFileSpecs();
        reaction(() => this.columnConfigurationSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), {fireImmediately: false, delay: 650 });
    }

    public addColumn() {
        this.showManageColumnComponent = true;
        this.manageColumnSpecComponentVM.initialise((this.fileSpecDropdownList.find(i => i.staticFileSpecID === this.columnConfigurationSearchCriteria.staticFileSpecID)?.columnCount! + 1), this.columnConfigurationSearchCriteria.staticFileSpecID!, this.columnConfigurationSearchCriteria.fileTypeConcatenated!);
    }

    public onBack() {
        this.navigation.navigateToView(FileSpecConfigurationView);
    }

    public async cancel() {
        this.manageColumnSpecComponentVM.managedColumnSpec = new ColumnConfigurationLookup();
        this.showManageColumnComponent = false;
    }

    public async clearSearch() {
        this.columnConfigurationSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async editColumnSpec(columnSpecLookup: ColumnConfigurationLookup) {
        this.manageColumnSpecComponentVM.managedColumnSpec.mapFrom(columnSpecLookup).toJSObject();
        this.showManageColumnComponent = true;
        this.manageColumnSpecComponentVM.initialise(this.manageColumnSpecComponentVM.managedColumnSpec.columnNumber);
    }

    public async loadFileSpecs() {
        const response = await this.taskRunner.waitFor(this.fileGroupingFileSpecApiClient.getFileSpecList(0!));
        this.fileSpecDropdownList.set(response.data);
    }

    public async saveColumnSpec() {
        this.taskRunner.run(async () => {
            var response = this.manageColumnSpecComponentVM.managedColumnSpec.staticColumnSpecID > 0
            ? await this.columnSpecApiClient.updateColumnSpec((this.mapColumnSpecToUpdateCommand(this.manageColumnSpecComponentVM.managedColumnSpec)!.toJSObject()))
            : await this.columnSpecApiClient.addNewColumnSpec((this.mapColumnSpecToCreateCommand(this.manageColumnSpecComponentVM.managedColumnSpec)!.toJSObject()));

            if (response.data.success) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.ColumnSaved, NotificationDuration.Standard);
                await this.pageManager.refreshData();
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }
            this.manageColumnSpecComponentVM.managedColumnSpec = new ColumnConfigurationLookup();
            this.showManageColumnComponent = false;
            await this.loadFileSpecs();
        })
    }

    private mapColumnSpecToCreateCommand(columnSpecLookup: ColumnConfigurationLookup) {
        var command = new CreateColumnSpecCommand().mapFrom(this.manageColumnSpecComponentVM.managedColumnSpec);
        return command;
    }

    private mapColumnSpecToUpdateCommand(columnSpecLookup: ColumnConfigurationLookup) {
        var command = new UpdateColumnSpecCommand().mapFrom(this.manageColumnSpecComponentVM.managedColumnSpec);
        return command;
    }
}